import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "../../lib/api";
import { useApp } from "../../lib/store";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Copy, KeyRound, Lock, Cable, ShieldAlert } from "lucide-react";

const IDP_TYPES = [
  ["entra_oidc", "Microsoft Entra ID (OIDC)"],
  ["google_oidc", "Google Workspace (OIDC)"],
  ["generic_oidc", "Generic OIDC"],
  ["generic_saml", "Generic SAML 2.0"],
];

function IdpTab() {
  const { t } = useApp();
  const [idps, setIdps] = useState([]);
  const [roles, setRoles] = useState([]);
  const [plants, setPlants] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/iam/idps").then((r) => setIdps(r.data || [])).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/iam/roles").then((r) => setRoles(r.data || [])).catch(() => {});
    api.get("/iam/plants?page_size=200").then((r) => setPlants(r.data.items || [])).catch(() => {});
  }, [load]);

  const save = async () => {
    try {
      const body = { ...editing, domains: (editing.domains_text || "").split(",").map((d) => d.trim()).filter(Boolean) };
      if (editing.id) await api.put(`/iam/idps/${editing.id}`, body);
      else await api.post("/iam/idps", body);
      toast.success("✓"); setEditing(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const open = (i) => setEditing(i ? { ...i, domains_text: (i.domains || []).join(", ") }
    : { name: "", type: "entra_oidc", enabled: false, domains_text: "", jit_enabled: true, group_mappings: [] });

  return (
    <div className="space-y-3" data-testid="auth-idp-tab">
      <div className="flex justify-end">
        <Button className="bg-[#E8871E] hover:bg-[#d1751a] text-white rounded-full" onClick={() => open(null)}
          data-testid="idp-new-btn"><Plus className="h-4 w-4 mr-1" />{t("iam_add_idp")}</Button>
      </div>
      <div className="space-y-2">
        {idps.map((i) => (
          <div key={i.id} className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`idp-row-${i.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <button className="font-semibold text-[#1B2A4A] hover:underline" onClick={() => open(i)}>{i.name}</button>
              <Badge variant="outline" className="text-[10px]">{i.type}</Badge>
              {i.enabled ? <Badge className="bg-emerald-50 text-emerald-700 hover:bg-inherit text-[10px]">aktiv</Badge>
                : <Badge className="bg-gray-100 text-gray-500 hover:bg-inherit text-[10px]">inaktiv</Badge>}
              {i.enforce_sso && <Badge className="bg-amber-50 text-amber-700 hover:bg-inherit text-[10px]">{t("iam_enforce_sso")}</Badge>}
              <button className="ml-auto text-gray-300 hover:text-red-500" data-testid={`idp-del-${i.id}`}
                onClick={async () => { await api.delete(`/iam/idps/${i.id}`); load(); }}><Trash2 className="h-4 w-4" /></button>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">{t("iam_domains")}: {(i.domains || []).join(", ") || "–"}</p>
            <div className="flex items-center gap-2 mt-2">
              <code className="text-[10px] bg-gray-50 border border-gray-100 rounded px-2 py-1 truncate flex-1">{i.callback_url}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(i.callback_url); toast.success("✓"); }}
                data-testid={`idp-copy-${i.id}`}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {!idps.length && <p className="text-sm text-gray-400">{t("iam_no_idps")}</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="idp-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? editing.name : t("iam_add_idp")}</DialogTitle>
            <DialogDescription>{t("iam_idp_help")}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">{t("name")}</Label>
                  <Input value={editing.name || ""} onChange={(e) => setEditing((x) => ({ ...x, name: e.target.value }))} data-testid="idp-name-input" /></div>
                <div className="space-y-1"><Label className="text-xs">{t("iam_idp_type")}</Label>
                  <Select value={editing.type} onValueChange={(v) => setEditing((x) => ({ ...x, type: v }))}>
                    <SelectTrigger data-testid="idp-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{IDP_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-xs">Client ID</Label>
                  <Input value={editing.client_id || ""} onChange={(e) => setEditing((x) => ({ ...x, client_id: e.target.value }))} data-testid="idp-client-id-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Client Secret</Label>
                  <Input type="password" placeholder={editing.has_secret ? "********" : ""}
                    onChange={(e) => setEditing((x) => ({ ...x, client_secret: e.target.value }))} data-testid="idp-secret-input" /></div>
                {editing.type === "entra_oidc" && (
                  <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Tenant ID</Label>
                    <Input value={editing.tenant_id || ""} onChange={(e) => setEditing((x) => ({ ...x, tenant_id: e.target.value }))} data-testid="idp-tenant-input" /></div>
                )}
                {(editing.type === "generic_oidc") && (
                  <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Discovery URL</Label>
                    <Input value={editing.discovery_url || ""} onChange={(e) => setEditing((x) => ({ ...x, discovery_url: e.target.value }))}
                      placeholder="https://idp.example.com/.well-known/openid-configuration" data-testid="idp-discovery-input" /></div>
                )}
                <div className="space-y-1 sm:col-span-2"><Label className="text-xs">{t("iam_domains")}</Label>
                  <Input value={editing.domains_text || ""} onChange={(e) => setEditing((x) => ({ ...x, domains_text: e.target.value }))}
                    placeholder="firma.de, tochter.com" data-testid="idp-domains-input" /></div>
                <div className="space-y-1 sm:col-span-2"><Label className="text-xs">{t("iam_jit_role")}</Label>
                  <Select value={editing.jit_role_id || "none"} onValueChange={(v) => setEditing((x) => ({ ...x, jit_role_id: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="idp-jit-role-select"><SelectValue placeholder="Read Only" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Read Only (Standard)</SelectItem>
                      {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm"><Switch checked={!!editing.enabled}
                  onCheckedChange={(v) => setEditing((x) => ({ ...x, enabled: v }))} data-testid="idp-enabled-switch" />{t("enabled")}</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={!!editing.enforce_sso}
                  onCheckedChange={(v) => setEditing((x) => ({ ...x, enforce_sso: v }))} data-testid="idp-enforce-switch" />{t("iam_enforce_sso")}</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={editing.jit_enabled !== false}
                  onCheckedChange={(v) => setEditing((x) => ({ ...x, jit_enabled: v }))} data-testid="idp-jit-switch" />{t("iam_jit")}</label>
              </div>
              {editing.enforce_sso && (
                <p className="text-[11px] text-amber-700 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded p-2">
                  <ShieldAlert className="h-4 w-4 shrink-0" />{t("iam_enforce_warning")}
                </p>
              )}
              <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                <p className="text-xs font-semibold text-[#1B2A4A]">{t("iam_group_mappings")}</p>
                {(editing.group_mappings || []).map((m, idx) => (
                  <div key={idx} className="flex flex-wrap gap-2 items-center" data-testid={`idp-mapping-${idx}`}>
                    <Input className="h-8 flex-1 min-w-[120px]" placeholder="IdP-Gruppe" value={m.group || ""}
                      onChange={(e) => setEditing((x) => {
                        const gm = [...x.group_mappings]; gm[idx] = { ...gm[idx], group: e.target.value }; return { ...x, group_mappings: gm };
                      })} />
                    <Select value={m.role_id || "none"} onValueChange={(v) => setEditing((x) => {
                      const gm = [...x.group_mappings]; gm[idx] = { ...gm[idx], role_id: v === "none" ? "" : v }; return { ...x, group_mappings: gm };
                    })}>
                      <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder={t("role")} /></SelectTrigger>
                      <SelectContent><SelectItem value="none">–</SelectItem>
                        {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={(m.plant_ids || [])[0] || "none"} onValueChange={(v) => setEditing((x) => {
                      const gm = [...x.group_mappings]; gm[idx] = { ...gm[idx], plant_ids: v === "none" ? [] : [v] }; return { ...x, group_mappings: gm };
                    })}>
                      <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder={t("iam_plants")} /></SelectTrigger>
                      <SelectContent><SelectItem value="none">–</SelectItem>
                        {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.code}</SelectItem>)}</SelectContent>
                    </Select>
                    <button className="text-gray-300 hover:text-red-500"
                      onClick={() => setEditing((x) => ({ ...x, group_mappings: x.group_mappings.filter((_, i) => i !== idx) }))}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <Button size="sm" variant="outline" data-testid="idp-add-mapping-btn"
                  onClick={() => setEditing((x) => ({ ...x, group_mappings: [...(x.group_mappings || []), { group: "", role_id: "", plant_ids: [] }] }))}>
                  <Plus className="h-3.5 w-3.5 mr-1" />{t("add")}
                </Button>
              </div>
              <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} data-testid="idp-save-btn">{t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SecurityTab() {
  const { t } = useApp();
  const [s, setS] = useState(null);
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    api.get("/iam/security-settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/iam/roles").then((r) => setRoles(r.data || [])).catch(() => {});
  }, []);
  const save = async () => {
    try { const { data } = await api.put("/iam/security-settings", s); setS(data); toast.success("✓"); }
    catch (e) { toast.error(errDetail(e)); }
  };
  if (!s) return <p className="text-sm text-gray-400">…</p>;
  const nums = [["password_min_length", "iam_pw_min"], ["idle_timeout_minutes", "iam_idle_timeout"],
    ["session_lifetime_hours", "iam_session_lifetime"], ["concurrent_session_limit", "iam_concurrent_sessions"],
    ["remember_me_days", "iam_remember_days"], ["invite_valid_days", "iam_invite_days"]];
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4 max-w-3xl" data-testid="auth-security-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {nums.map(([k, label]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs">{t(label)}</Label>
            <Input type="number" value={s[k]} onChange={(e) => setS((x) => ({ ...x, [k]: Number(e.target.value) }))}
              data-testid={`sec-${k}-input`} />
          </div>
        ))}
      </div>
      <label className="flex items-center justify-between text-sm max-w-xs">
        {t("iam_breach_check")}
        <Switch checked={!!s.breach_check} onCheckedChange={(v) => setS((x) => ({ ...x, breach_check: v }))} data-testid="sec-breach-switch" />
      </label>
      <div className="space-y-1">
        <Label className="text-xs">{t("iam_mfa_roles")}</Label>
        <div className="flex flex-wrap gap-1.5">
          {roles.map((r) => (
            <button key={r.key} type="button" data-testid={`sec-mfa-role-${r.key}`}
              onClick={() => setS((x) => ({ ...x, mfa_enforced_roles: (x.mfa_enforced_roles || []).includes(r.key)
                ? x.mfa_enforced_roles.filter((k) => k !== r.key) : [...(x.mfa_enforced_roles || []), r.key] }))}
              className={`px-2.5 h-7 rounded-full text-[11px] border ${(s.mfa_enforced_roles || []).includes(r.key)
                ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white text-gray-600 border-gray-200"}`}>{r.name}</button>
          ))}
        </div>
      </div>
      <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} data-testid="sec-save-btn">{t("save")}</Button>
    </div>
  );
}

function ScimTab() {
  const { t } = useApp();
  const [s, setS] = useState(null);
  const load = () => api.get("/iam/scim-settings").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const put = async (body) => {
    try { const { data } = await api.put("/iam/scim-settings", body); setS(data); toast.success("✓"); }
    catch (e) { toast.error(errDetail(e)); }
  };
  if (!s) return <p className="text-sm text-gray-400">…</p>;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4 max-w-2xl" data-testid="auth-scim-tab">
      <label className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2"><Cable className="h-4 w-4 text-[#E8871E]" />{t("iam_scim_enable")}</span>
        <Switch checked={!!s.enabled} onCheckedChange={(v) => put({ enabled: v })} data-testid="scim-enable-switch" />
      </label>
      <div className="space-y-1">
        <Label className="text-xs">SCIM Base URL</Label>
        <code className="block text-[11px] bg-gray-50 border border-gray-100 rounded px-2 py-1.5" data-testid="scim-base-url">
          {window.location.origin.replace(/\/$/, "")}/api/scim/v2
        </code>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("iam_scim_token")}</Label>
        <div className="flex gap-2">
          <Input readOnly value={s.token || ""} className="font-mono text-xs" data-testid="scim-token-input" />
          <Button variant="outline" onClick={() => put({ enabled: s.enabled, rotate_token: true })} data-testid="scim-rotate-btn">
            <KeyRound className="h-3.5 w-3.5 mr-1" />{t("iam_rotate_token")}
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-gray-500">{t("iam_scim_help")}</p>
    </div>
  );
}

function LoginAuditTab() {
  const { t } = useApp();
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const load = useCallback(() => {
    const p = new URLSearchParams({ limit: 200 });
    if (email) p.set("email", email);
    api.get(`/iam/login-audit?${p}`).then((r) => setRows(r.data || [])).catch(() => {});
  }, [email]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  return (
    <div className="space-y-3" data-testid="auth-login-audit-tab">
      <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-Mail" className="max-w-xs h-9"
        data-testid="login-audit-email-input" />
      <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-50 max-h-[60vh] overflow-y-auto">
        {rows.map((l) => (
          <div key={l.id} className="px-3 py-2 text-xs flex flex-wrap gap-2 items-center" data-testid={`login-audit-row-${l.id}`}>
            <span className={l.success ? "text-emerald-600" : "text-red-600"}>{l.success ? "✓" : "✕"}</span>
            <span className="font-medium">{l.email}</span>
            <Badge variant="outline" className="text-[10px]">{l.method}</Badge>
            {l.reason && <span className="text-gray-400">{l.reason}</span>}
            <span className="font-mono text-gray-400">{l.ip}</span>
            <span className="ml-auto font-mono text-gray-400">{(l.at || "").slice(0, 16).replace("T", " ")}</span>
          </div>
        ))}
        {!rows.length && <p className="px-3 py-4 text-xs text-gray-400">–</p>}
      </div>
    </div>
  );
}

export default function AuthSecurity() {
  const { t } = useApp();
  return (
    <div className="space-y-4" data-testid="iam-auth-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }}>{t("iam_auth")}</h1>
        <p className="text-xs text-gray-500">{t("iam_auth_sub")}</p>
      </div>
      <Tabs defaultValue="idps">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="idps" data-testid="auth-tab-idps"><Lock className="h-3.5 w-3.5 mr-1" />{t("iam_idps")}</TabsTrigger>
          <TabsTrigger value="security" data-testid="auth-tab-security">{t("iam_security")}</TabsTrigger>
          <TabsTrigger value="scim" data-testid="auth-tab-scim">SCIM</TabsTrigger>
          <TabsTrigger value="logins" data-testid="auth-tab-logins">{t("iam_login_audit")}</TabsTrigger>
        </TabsList>
        <TabsContent value="idps" className="pt-4"><IdpTab /></TabsContent>
        <TabsContent value="security" className="pt-4"><SecurityTab /></TabsContent>
        <TabsContent value="scim" className="pt-4"><ScimTab /></TabsContent>
        <TabsContent value="logins" className="pt-4"><LoginAuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}
